const generateSrcset = (image, widths = []) => {
  const imageUrl = new URL(image["src"]);
  return widths.filter((width) => width <= image["width"]).map((width) => {
    imageUrl.searchParams.set("width", width.toString());
    return `${imageUrl.href} ${width}w`;
  }).join(", ");
}

const createImageElement = (image, classes, sizes, productTitle) => {
  const previewImage = image["preview_image"];
  const newImage = new Image(previewImage["width"], previewImage["height"]);
  newImage.className = classes;
  newImage.alt = image["alt"] || productTitle;
  newImage.sizes = sizes;
  newImage.src = previewImage["src"];
  newImage.srcset = generateSrcset(previewImage, [165, 360, 533, 720, 940, 1066]);
  newImage.loading="lazy"
  return newImage;
}

const productCache = new Map();

const getHandleFromProductUrl = (url) => {
  if (!url) return null;

  try {
    const normalizedUrl = new URL(url, window.location.origin);
    const match = normalizedUrl.pathname.match(/\/products\/([^/]+)/);
    return match ? match[1] : null;
  } catch (error) {
    return null;
  }
}

const getProductData = async (handle) => {
  if (!handle) return null;

  if (productCache.has(handle)) {
    return productCache.get(handle);
  }

  const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);
  if (!response.ok) {
    throw new Error(`Failed to load product data for handle: ${handle}`);
  }

  const product = await response.json();
  productCache.set(handle, product);
  return product;
}

const stripHtml = (html) => {
  const temp = document.createElement("div");
  temp.innerHTML = html || "";
  return (temp.textContent || temp.innerText || "").trim();
}

const truncateText = (text, limit) => {
  if (!text || text.length <= limit) return text;
  return `${text.slice(0, limit).trimEnd()}...`;
}

const formatVariantMoney = (cents) => {
  if (typeof formatMoney === 'function') {
    const moneyFormat = window?.theme?.currencyCodeEnabled
      ? (window?.theme?.moneyWithCurrencyFormat || window?.theme?.moneyFormat)
      : window?.theme?.moneyFormat;

    return formatMoney(cents, moneyFormat);
  }

  const normalizedCents = Number(cents) || 0;
  return (normalizedCents / 100).toFixed(2);
}

const getCardVariantTitle = (product, variant) => {
  if (!product) return "";

  if (!variant || !variant.title || variant.title === "Default Title") {
    return product.title;
  }

  return `${product.title} - ${variant.title}`;
}

const updateCardLinks = (wrapper, variantLink) => {
  if (!variantLink) return;

  const overlayLink = wrapper.querySelector('.js-color-swatches-link');
  const titleLink = wrapper.querySelector('.card__title a');

  if (overlayLink) {
    overlayLink.setAttribute('href', variantLink);
  }

  if (titleLink) {
    titleLink.setAttribute('href', variantLink);
  }
}

const updateCardText = (wrapper, product, variant) => {
  const titleLink = wrapper.querySelector('.card__title a');
  const description = wrapper.querySelector('.card__description');
  const cardTitle = getCardVariantTitle(product, variant);

  if (titleLink && cardTitle) {
    titleLink.textContent = cardTitle;
    titleLink.setAttribute('title', cardTitle);
  }

  if (description) {
    description.textContent = truncateText(stripHtml(product.description), 120);
  }
}

const updateCardPrice = (wrapper, variant) => {
  const priceContainer = wrapper.querySelector('.price');

  if (!priceContainer || !variant) {
    return;
  }

  const regularPriceElement = priceContainer.querySelector('.price__regular .price-item--regular');
  const salePriceElement = priceContainer.querySelector('.price__sale .price-item--sale');
  const comparePriceElement = priceContainer.querySelector('.price__compare .price-item--regular');

  const formattedPrice = formatVariantMoney(variant.price);
  const hasComparePrice = Number(variant.compare_at_price) > Number(variant.price);

  if (regularPriceElement) {
    regularPriceElement.textContent = formattedPrice;
  }

  if (salePriceElement) {
    salePriceElement.textContent = formattedPrice;
  }

  if (comparePriceElement && hasComparePrice) {
    comparePriceElement.textContent = formatVariantMoney(variant.compare_at_price);
  }

  priceContainer.classList.toggle('price--on-sale', hasComparePrice);
  priceContainer.classList.toggle('price--sold-out', variant.available === false);
}

const updateCardImage = (wrapper, product, variant) => {
  const primaryImage = wrapper.querySelector(".media--first");
  const secondaryImage = wrapper.querySelector(".media--second");

  if (!primaryImage || !variant || !variant.featured_media) {
    return;
  }

  const newPrimaryImage = createImageElement(variant.featured_media, primaryImage.className, primaryImage.sizes, product.title);

  if (newPrimaryImage.src === primaryImage.src) {
    return;
  }

  let useSecondaryAsPrimary = false;

  if (secondaryImage) {
    const secondaryImagePathname = new URL(secondaryImage.src).pathname;
    const newPrimaryImagePathname = new URL(newPrimaryImage.src).pathname;

    if (secondaryImagePathname === newPrimaryImagePathname) {
      primaryImage.remove();
      secondaryImage.classList.remove('media--second');
      secondaryImage.classList.add('media--first');
      useSecondaryAsPrimary = true;
    }
  }

  if (!useSecondaryAsPrimary) {
    primaryImage.animate({ opacity: [1, 0] }, { duration: 200, easing: "ease-in", fill: "forwards" }).finished;
    setTimeout(function(){
      primaryImage.replaceWith(newPrimaryImage);
      newPrimaryImage.animate({ opacity: [0, 1] }, { duration: 200, easing: "ease-in" });
      if (secondaryImage) {
        secondaryImage.remove();
      }
    }, 200);
  }
}

const getSelectedOptionsByPosition = (wrapper) => {
  const selectedOptions = {};

  wrapper.querySelectorAll('.js-variant-options input:checked[data-option-position]').forEach((input) => {
    const position = Number(input.dataset.optionPosition);

    if (position > 0) {
      selectedOptions[position] = input.value;
    }
  });

  return selectedOptions;
}

const findVariantBySelectedOptions = (product, selectedOptions) => {
  const selectedPositions = Object.keys(selectedOptions);

  if (!selectedPositions.length || !product?.variants?.length) {
    return null;
  }

  return product.variants.find((variant) => {
    return selectedPositions.every((positionKey) => {
      const position = Number(positionKey);
      return variant.options[position - 1] === selectedOptions[position];
    });
  }) || null;
}

const checkSwatches = () => {
  document.querySelectorAll('.js-color-swatches-wrapper').forEach(wrapper => {
    if (wrapper.dataset.swatchesBound === 'true') {
      return;
    }

    wrapper.dataset.swatchesBound = 'true';

    wrapper.addEventListener('change', async (event) => {
        const inputTarget = event.target.closest('.js-color-swatches input, .js-variant-options input');

        if (!inputTarget || !wrapper.contains(inputTarget)) {
          return;
        }

        if (!inputTarget.checked) {
          return;
        }

        const baseHandle = wrapper.dataset.baseProduct || wrapper.dataset.product;
        if (!wrapper.dataset.baseProduct && baseHandle) {
          wrapper.dataset.baseProduct = baseHandle;
        }

        const variantLink = inputTarget.dataset.variantLink;
        const variantId = Number(inputTarget.dataset.variantId);
        const optionPosition = Number(inputTarget.dataset.optionPosition);
        const nextHandle = baseHandle || getHandleFromProductUrl(variantLink);

        if (!nextHandle) {
          return;
        }

        if (variantLink) {
          updateCardLinks(wrapper, variantLink);
        }

        if (wrapper.querySelector('.card__add-to-cart button[name="add"]')) {
          wrapper.querySelector('.card__add-to-cart button[name="add"]').setAttribute('aria-disabled', false);
          if (wrapper.querySelector('.card__add-to-cart button[name="add"] > span')) {
            wrapper.querySelector('.card__add-to-cart button[name="add"] > span').classList.remove('hidden');
            wrapper.querySelector('.card__add-to-cart button[name="add"] .sold-out-message').classList.add('hidden');
          }
          wrapper.querySelector('.card__add-to-cart input[name="id"]').value = inputTarget.dataset.variantId;
        }

        try {
          const product = await getProductData(nextHandle);
          if (!product) return;

          const selectedOptions = getSelectedOptionsByPosition(wrapper);

          const selectedVariant = findVariantBySelectedOptions(product, selectedOptions)
            || product.variants.find(item => Number(item.id) === variantId)
            || product.variants.find(item => optionPosition > 0 && item.options[optionPosition - 1] === inputTarget.value)
            || product.variants.find(item => item.featured_media != null && item.options.includes(inputTarget.value))
            || product.variants[0];

          if (!selectedVariant) {
            return;
          }

          updateCardLinks(wrapper, selectedVariant.url || variantLink);
          updateCardText(wrapper, product, selectedVariant);
          updateCardPrice(wrapper, selectedVariant);
          updateCardImage(wrapper, product, selectedVariant);
          if (wrapper.querySelector('.card__add-to-cart input[name="id"]')) {
            wrapper.querySelector('.card__add-to-cart input[name="id"]').value = selectedVariant.id;
          }
          wrapper.dataset.variant = String(selectedVariant.id);
        } catch (error) {
          console.error('Unable to update swatch selection', error);
        }
      });
  })
}

function colorSwatches() {
  checkSwatches();

  document.addEventListener('shopify:section:load', function () {
    checkSwatches();
  });
}

window.colorSwatches = colorSwatches;

(function () {
colorSwatches();
})()